import React from 'react';
import PropTypes from 'prop-types';
import {
  TitleHeaderContainer,
  TitleHeaderContent,
  TitleHeaderContentLabel
} from './TitleHeader.style';

/**
 * The TitleHeader is a specific styled header with the function of a title.
 * This TitleHeader has 2 style variants: 'primary' and 'secondary'
 *
 * TitleHeaders can be used nested as deep as you want. Indentation on the right to indicate the level of the header can be achieved by using padding.
 *
 * The primary and secondary style variant have their specific label styling. This label is filled in by the label prop. If you are in need of a custom label style with e.g. use of an icon in front, you can achieve this by leaving the label prop empty and writing the component with a closing tag with the html string for your custom label between the tags of the TitleHeader.
 *
 *
 */

// eslint-disable-next-line no-underscore-dangle
export const _TitleHeader = ({
  label = '',
  children = null,
  innerRef = { current: null },
  className = '',
  variant = 'primary',
  truncate = false
}) => {
  return (
    <TitleHeaderContainer data-test-type="titleheader" ref={innerRef} $variant={variant}>
      <TitleHeaderContent className={className} $variant={variant}>
        {label && !children && (
          <TitleHeaderContentLabel
            $variant={variant}
            data-test-type="title"
            $transform="uppercase"
            $truncate={truncate}
          >
            {label}
          </TitleHeaderContentLabel>
        )}
        {children && children}
      </TitleHeaderContent>
    </TitleHeaderContainer>
  );
};

_TitleHeader.propTypes = {
  label: PropTypes.string,
  variant: PropTypes.oneOf(['primary', 'secondary']),
  children: PropTypes.oneOfType([PropTypes.node, PropTypes.arrayOf(PropTypes.node)]),
  /** ClassName is supported such that styled components can pass extra styling.
   * Do not use this to adjust the styling, because the ordering of the applied tailwind classes is undeterministic */
  className: PropTypes.string,
  innerRef: PropTypes.shape({}),
  truncate: PropTypes.bool
};

export default React.forwardRef((props, ref) => (
  // eslint-disable-next-line react/jsx-pascal-case
  <_TitleHeader innerRef={ref} {...props} />
));
